import { action, observable, computed } from 'mobx'
import { type WorkspacePostingOpportunityViewModel } from './WorkspacePostingOpportunityViewModel'
import { task } from 'mobx-task'
import type { AlertErrorHandlerFactory } from '@/features/shell'
import { type WorkspacePostingOpportunityStore } from './WorkspacePostingOpportunityStore'

/**
 * View model for the selected workspace posting opportunity.
 */
export class WorkspaceOpportunityDetailsViewModel {
  /**
   * The selected opportunity.
   */
  @observable accessor selected: WorkspacePostingOpportunityViewModel | null =
    null

  constructor(
    private readonly _workspacePostingOpportunityStore: WorkspacePostingOpportunityStore,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * Whether the selected opportunity is active.
   */
  @computed
  get selectedIsActive() {
    return this.selected?.opportunity.status === 'Active'
  }

  /**
   * Whether selection is disabled.
   */
  @computed
  get disabled() {
    return this.apply.pending || this.withdraw.pending
  }

  /**
   * Selects or unselects an opportunity.
   *
   * @param opportunityViewModel
   */
  @action.bound
  select(opportunityViewModel: WorkspacePostingOpportunityViewModel | null) {
    this.selected = opportunityViewModel
  }

  /**
   * Deselects the selected opportunity.
   */
  @action.bound
  deselect() {
    this.select(null)
  }

  /**
   * Applies to the selected opportunity.
   */
  apply = task.resolved(async () => {
    if (!this.selected) {
      return
    }
    await this._workspacePostingOpportunityStore
      .applyToOpportunity(this.selected.opportunityId)
      .catch(this._createAlertErrorHandler('Applying to the posting failed'))
  })

  /**
   * Withdraws from the selected opportunity, deselecting it when it is no longer active.
   */
  withdraw = task.resolved(async () => {
    if (!this.selected) {
      return
    }
    await this._workspacePostingOpportunityStore
      .withdrawFromOpportunity(this.selected.opportunityId)
      .catch(
        this._createAlertErrorHandler('Withdrawing from the posting failed'),
      )
    if (!this.selectedIsActive) {
      this.deselect()
    }
  })
}
